import type { PageSeoData } from '@do/svelte-seo';

type Pages =
  | 'main'
  | 'artworks'
  | 'auctions'
  | 'artists'
  | 'artist'
  | 'aboutUs'
  | 'contactUs'
  | 'faq'
  | 'eula'
  | 'termsOfUse'
  | 'artwork'
  | 'auction'
  | 'walletAuth'
  | 'behindTheScenes'
  | 'news'
  | 'news-article'
  | 'curators'
  | 'curator'
  | 'selections'
  | 'selection'
  | 'selectionAuth'
  | 'privacyPolicy';

interface Params extends Partial<PageSeoData> {
  galleryName?: string;
  artworkName?: string;
  artistName?: string;
  auctionName?: string;
  curatorName?: string;
  selectionTitle?: string;
}

export const defaultTitle = 'Digital Original store';
export const defaultMetaDescription =
  'Digital Original is an all-in-one platform for digitizing, cataloging, and selling artworks in digital format. With DLCT technology, we ensure high-quality digitization, making your art accessible to a global audience.';

export const seoConfig: { [key in Pages]: (params?: Params) => PageSeoData } = {
  main({ breadcrumbs, robots = true } = {}) {
    return {
      title: `UFDA - Ukrainian Fund of Digitized Art: popular artworks, ongoing auctions, artist, contact information - on the Digital Original Store`,
      metaDescription: `✓ Discover exquisite art pieces showcased at Ukrainian Fund of Digitized Art (UFDA) ✓ Explore our curated selection ✓ Learn about upcoming auctions ✓ Find out more about Ukrainian Fund of Digitized Art ✓ Immerse yourself in the world of art in digital with the Digital Original`,
      breadcrumbs,
      robots,
    };
  },
  artworks({ breadcrumbs, robots = true, title, metaDescription } = {}) {
    return {
      title: title || `Explore and Purchase Digital Originals from the Ukrainian Fund of Digitized Art (UFDA)`,
      metaDescription:
        metaDescription ||
        `Discover and purchase unique digital originals from the Ukrainian Fund of Digitized Art (UFDA). Explore a curated collection of contemporary Ukrainian artworks, digitized and ready to own, supporting artists and cultural preservation`,
      breadcrumbs,
      robots,
    };
  },
  artwork(
    { artworkName, artistName, metaImage, breadcrumbs, robots = true } = {
      artworkName: '',
      artistName: '',
      robots: true,
    },
  ) {
    return {
      title: `Digital Original of ${artworkName} by ${artistName}`,
      metaDescription: `✓ Acquire ${artworkName} by ${artistName} ✓ Explore unique creations from top artists in Ukrainian Fund of Digitized Art (UFDA) ✓ Discover popular artworks at Digital Original`,
      metaImage,
      breadcrumbs,
      robots,
    };
  },
  auctions({ breadcrumbs, robots = true } = {}) {
    return {
      title: `Bid on Unique Digital Originals at UFDA's Art Auctions`,
      metaDescription: `Join UFDA's Art Auctions to bid on exclusive digital originals of contemporary Ukrainian artworks. Own a piece of culture while supporting artists and creative preservation`,
      breadcrumbs,
      robots,
    };
  },
  auction({ auctionName, artistName, metaImage, breadcrumbs, robots = true } = { artistName: '', auctionName: '' }) {
    return {
      title: `Join the digital auction of ${auctionName} by ${artistName} in Digital Original format and place your bid`,
      metaDescription: `Available for sale ${auctionName} from ${artistName} ✓ Join the auction to acquire a digital original of artwork ✓ Enrich your collection`,
      h1: `Auction for artwork ${auctionName}`,
      metaImage,
      breadcrumbs,
      robots,
    };
  },
  artists({ breadcrumbs, robots = true, title, metaDescription } = {}) {
    return {
      title: title || `Explore the Artists of the Ukrainian Fund of Digitized Art (UFDA)`,
      metaDescription:
        metaDescription ||
        `Explore the profiles of talented artists featured in the Ukrainian Fund of Digitized Art (UFDA). Learn about their work, stories, and contributions to digital art and cultural preservation`,
      breadcrumbs,
      robots,
    };
  },
  artist({ artistName, metaImage, breadcrumbs, robots = true, title, metaDescription } = { artistName: '' }) {
    return {
      title:
        title ||
        `${artistName}: Biography, Artworks, and Digitized Art in the Digital Original Format for Sale in the Ukrainian Fund of Digitized Art (UFDA) Store`,
      metaDescription:
        metaDescription ||
        `Explore the vibrant art of ${artistName} ✓ Find ${artistName} artworks available for purchase in the Ukrainian Fund of Digitized Art (UFDA) ✓ Discover unique digital originals ✓ Convert ${artistName} art into digital originals`,
      metaImage,
      breadcrumbs,
      robots,
    };
  },
  aboutUs({ breadcrumbs, robots = true } = {}) {
    return {
      title: `Learn More About the Ukrainian Fund of Digitized Art (UFDA)`,
      metaDescription: `Discover the mission and vision of the Ukrainian Fund of Digitized Art (UFDA). Learn how we celebrate and preserve Ukrainian art through digitization, supporting artists and cultural heritage`,
      breadcrumbs,
      robots,
    };
  },
  contactUs({ breadcrumbs, robots = true } = {}) {
    return {
      title: `Contact Us. Ukrainian Fund of Digitized Art (UFDA)`,
      metaDescription: `Have questions or need assistance? Contact the Ukrainian Fund of Digitized Art (UFDA) team for support, inquiries, and collaboration opportunities. We’re here to help!`,
      breadcrumbs,
      robots,
    };
  },
  faq({ breadcrumbs, robots = true } = { breadcrumbs: [] }) {
    return {
      title: 'FAQ of the Ukrainian Fund of Digitized Art (UFDA) – Your Questions Answered',
      metaDescription:
        'Find answers to common questions about the Ukrainian Fund of Digitized Art (UFDA). Learn about how to purchase digital originals, how to join auctions, and how to support Ukrainian artists',
      breadcrumbs,
      robots,
    };
  },
  eula({ breadcrumbs, robots = true } = {}) {
    return {
      title: `End-User License Agreement (EULA) – Ukrainian Fund of Digitized Art (UFDA)`,
      breadcrumbs,
      robots,
    };
  },
  termsOfUse({ breadcrumbs, robots = true } = {}) {
    return {
      title: `Terms of Use | Your Guide to UFDA’s Website Policies`,
      metaDescription:
        'Understand the rules and guidelines for using UFDA’s website. Our Terms of Use cover user responsibilities, intellectual property, and more. Stay informed and compliant with our policies.',
      breadcrumbs,
      robots,
    };
  },
  walletAuth({ breadcrumbs } = { breadcrumbs: [] }) {
    return {
      title: 'Wallet authorization – Digital Original',
      metaDescription: 'Authorize your wallet to interact with Digital Original',
      breadcrumbs,
    };
  },
  behindTheScenes({ breadcrumbs, robots = true } = { breadcrumbs: [] }) {
    return {
      title: 'Behind the Scenes: Preserving Ukrainian Art Heritage Through Digitization',
      metaDescription:
        'Discover the process of digitizing Ukrainian artworks using advanced DLCT technology. Explore how this initiative preserves Ukraine’s cultural heritage and brings its art into the digital age.',
      breadcrumbs,
      robots,
    };
  },
  news({ breadcrumbs, robots = true } = { breadcrumbs: [] }) {
    return {
      title: 'News from the Ukrainian Fund of Digitized Art (UFDA) – Stay Updated on the Latest Art Developments',
      metaDescription:
        'Stay informed on the latest news and updates from the Ukrainian Fund of Digitized Art (UFDA). Learn about new artworks, upcoming auctions, artist features, and more in the digital art world.',
      breadcrumbs,
      robots,
    };
  },
  'news-article'({ breadcrumbs, title, metaImage, robots = true } = { breadcrumbs: [], title: '' }) {
    return {
      title: `UFDA: ${title}`,
      metaDescription: `Learn more about ${title} at u-f-d-a.org. Discover now!”`,
      breadcrumbs,
      metaImage,
      robots,
    };
  },
  curators({ breadcrumbs, robots = true } = {}) {
    return {
      title: `Curators of Ukrainian Fund of Digitized Art`,
      metaDescription: `Explore curators collaborating with leading Ukrainian artists to provide insightful commentary, offering deeper context and a unique perspective on digital counterparts.`,
      breadcrumbs,
      robots,
    };
  },
  curator({ curatorName, breadcrumbs, robots = true } = { curatorName: '', breadcrumbs: [] }) {
    return {
      title: `${curatorName} is the curator at the Ukrainian Fund of Digital Art (UFDA).`,
      metaDescription: `${curatorName} is the curator at the Ukrainian Fund of Digital Art (UFDA), overseeing a vast collection of digitized Ukrainian artworks. Discover their role in preserving cultural heritage and explore the UFDA’s unique digital archive.`,
      breadcrumbs,
      robots,
    };
  },
  selections({ breadcrumbs, robots = true } = {}) {
    return {
      title: `Curated Selection of Ukrainian Artworks | Explore Unique Masterpieces.`,
      metaDescription: `Browse UFDA’s curated selection of Ukrainian artworks. Discover stunning masterpieces by top artists, digitized in 400MPX quality. Celebrate art and culture with us today.`,
      breadcrumbs,
      robots,
    };
  },
  selection({ selectionTitle, breadcrumbs, robots = true } = { selectionTitle: '', breadcrumbs: [] }) {
    return {
      title: `Discover the Selection of the Artworks ${selectionTitle} at the Ukrainian Fund of Digitised Art (UFDA)`,
      metaDescription: `Discover and purchase unique digital originals from the Ukrainian Fund of Digitized Art (UFDA). Explore a curated collection of contemporary Ukrainian artworks, digitized and ready to own, supporting artists and cultural preservation`,
      breadcrumbs,
      robots,
    };
  },
  selectionAuth({ breadcrumbs } = { breadcrumbs: [] }) {
    return {
      title: `Discover the Private Selection of the Artworks at the Ukrainian Fund of Digitised Art (UFDA).`,
      metaDescription: `Discover the Private Selection of Artworks at the Ukrainian Fund of Digitised Art (UFDA). Explore exclusive pieces created by contemporary Ukrainian artists in the Digital Original Standard`,
      breadcrumbs,
    };
  },
  privacyPolicy({ breadcrumbs, robots = true } = { breadcrumbs: [] }) {
    return {
      title: 'Privacy Policy | Protecting Your Data at UFDA',
      metaDescription:
        'Discover how UFDA safeguards your personal information. Read our Privacy Policy to learn about data collection, usage, and your rights. Transparent, secure, and user-focused.',
      breadcrumbs,
      robots,
    };
  },
};

export function getPaginatedSeoData({
  page,
  title,
  metaDescription,
}: {
  page: number;
  title: string;
  metaDescription: string;
}): PageSeoData {
  if (page <= 1) {
    return {};
  }

  return {
    title: `Page - ${page} | ${title}`,
    metaDescription: `Page - ${page} | ${metaDescription}`,
  };
}
